import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { format } from 'date-fns';
import { getInventoryAdjustments, InventoryAdjustment } from '../../firebase/inventoryAdjustmentServices';
import { getAllProductCosts, ProductCost } from '../../firebase/inventoryCostServices';
import { formatCurrency } from '../../utils/formatters';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

interface ProductMovement {
  productId: string;
  productName: string;
  productSku: string;
  beginningStock: number;
  salesMovement: number;
  purchaseMovement: number;
  manualMovement: number;
  endingStock: number;
  beginningValue: number;
  endingValue: number;
}

interface MonthlyInventorySummaryProps {
  year: number;
  month: number;
}

const MonthlyInventorySummary: React.FC<MonthlyInventorySummaryProps> = ({ year, month }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [productMovements, setProductMovements] = useState<ProductMovement[]>([]);
  const [filteredMovements, setFilteredMovements] = useState<ProductMovement[]>([]);
  const [adjustments, setAdjustments] = useState<InventoryAdjustment[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Fetch products and inventory adjustments for the current month and previous months
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Set date range for the current month
        const currentMonthStart = new Date(year, month, 1);
        const currentMonthEnd = new Date(year, month + 1, 0);
        
        // Get all products from the main-app's products collection
        const productsQuery = query(collection(db, 'products'));
        const productsSnapshot = await getDocs(productsQuery);
        const allProducts: Array<any> = [];
        
        productsSnapshot.forEach((doc) => {
          allProducts.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setProducts(allProducts);
        console.log(`Loaded ${allProducts.length} products from the database`);
        
        // Get all adjustments (we'll filter client-side)
        // Increased limit to ensure we get enough history
        const data = await getInventoryAdjustments(2000);
        
        // Create a map to track the last known state of each product before the current month
        const previousMonthEndingState = new Map<string, { stock: number, value: number }>();
        
        // Filter and sort all adjustments before the current month
        const previousAdjustments = data
          .filter(adjustment => {
            const adjustmentDate = adjustment.adjustmentDate instanceof Date 
              ? adjustment.adjustmentDate 
              : new Date((adjustment.adjustmentDate as any).seconds * 1000);
            
            return adjustmentDate < currentMonthStart;
          })
          .sort((a, b) => {
            // Sort by date (oldest first)
            const dateA = a.adjustmentDate instanceof Date 
              ? a.adjustmentDate 
              : new Date((a.adjustmentDate as any).seconds * 1000);
            const dateB = b.adjustmentDate instanceof Date 
              ? b.adjustmentDate 
              : new Date((b.adjustmentDate as any).seconds * 1000);
            return dateA.getTime() - dateB.getTime();
          });
        
        // Process all previous adjustments to build up the ending state before current month
        previousAdjustments.forEach(adjustment => {
          adjustment.items.forEach(item => {
            const productId = item.productId;
            const currentState = previousMonthEndingState.get(productId) || { stock: 0, value: 0 };
            
            // For each adjustment, update the running total
            // Instead of just taking the last value, we need to properly account for all movements
            const quantityChange = item.quantityChange || 0;
            const valueChange = item.valueChange || 0;
            
            // Update the state for this product
            previousMonthEndingState.set(productId, {
              stock: item.quantityAfter || (currentState.stock + quantityChange),
              value: currentState.value + valueChange
            });
          });
        });
        
        // Filter for the current month's adjustments
        const currentMonthAdjustments = data.filter(adjustment => {
          const adjustmentDate = adjustment.adjustmentDate instanceof Date 
            ? adjustment.adjustmentDate 
            : new Date((adjustment.adjustmentDate as any).seconds * 1000);
          
          return adjustmentDate >= currentMonthStart && adjustmentDate <= currentMonthEnd;
        }).sort((a, b) => {
          // Sort by date (oldest first)
          const dateA = a.adjustmentDate instanceof Date 
            ? a.adjustmentDate 
            : new Date((a.adjustmentDate as any).seconds * 1000);
          const dateB = b.adjustmentDate instanceof Date 
            ? b.adjustmentDate 
            : new Date((b.adjustmentDate as any).seconds * 1000);
          return dateA.getTime() - dateB.getTime();
        });
        
        setAdjustments(currentMonthAdjustments);
        calculateProductMovements(allProducts, currentMonthAdjustments, previousAdjustments, previousMonthEndingState);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [year, month]);
  
  // Calculate product movements from adjustments
  const calculateProductMovements = (
    products: any[],
    currentMonthAdjustments: InventoryAdjustment[],
    previousAdjustments: InventoryAdjustment[], 
    previousMonthEndingState: Map<string, { stock: number, value: number }>
  ) => {
    const productMap = new Map<string, ProductMovement>();
    
    // First, initialize all products from the main-app's products collection
    products.forEach(product => {
      const productId = product.id;
      if (!productId) return; // Skip products without an ID
      
      // Get previous month ending state if available
      const previousState = previousMonthEndingState.get(productId);
      
      // Beginning stock should be the previous month's ending stock, or 0 if no previous data
      // NOT the current stock value from the product
      const beginningStock = previousState ? previousState.stock : 0;
      const beginningValue = previousState ? previousState.value : 0;
      
      // Initialize ending stock to beginning stock (will be updated later)
      const endingStock = beginningStock;
      
      productMap.set(productId, {
        productId,
        productName: product.name || 'Unknown Product',
        productSku: product.sku || '',
        beginningStock,
        salesMovement: 0,
        purchaseMovement: 0,
        manualMovement: 0,
        endingStock, // Initialize ending stock with beginning stock
        beginningValue,
        endingValue: beginningValue
      });
    });
    
    // Process current month adjustments
    currentMonthAdjustments.forEach(adjustment => {
      adjustment.items.forEach(item => {
        const productId = item.productId;
        
        // Get or create product movement record
        let movement = productMap.get(productId);
        if (!movement) {
          // This is a product that exists in transactions but not in the product database
          // This could happen if a product was deleted from the database but still has transactions
          movement = {
            productId,
            productName: item.productName,
            productSku: item.productSku || '',
            beginningStock: 0, // New product starts with 0
            salesMovement: 0,
            purchaseMovement: 0,
            manualMovement: 0,
            endingStock: 0,
            beginningValue: 0,
            endingValue: 0
          };
          productMap.set(productId, movement);
        }
        
        // Determine movement type and update accordingly
        const quantityChange = item.quantityChange;
        const subtype = item.subtype || '';
        const reason = adjustment.reason.toLowerCase();
        
        if (subtype === 'sale_deduction' || reason.includes('sale:')) {
          movement.salesMovement += quantityChange;
        } else if (subtype === 'purchase_receipt' || reason.includes('purchase receipt')) {
          movement.purchaseMovement += quantityChange;
        } else {
          movement.manualMovement += quantityChange;
        }
        
        // We'll calculate the ending stock at the end based on all movements
      });
    });
    
    // Calculate ending stock for each product based on all movements
    productMap.forEach(movement => {
      // Ending stock = Beginning stock + Purchases + Manual Adjustments - Sales
      movement.endingStock = movement.beginningStock + movement.purchaseMovement + movement.manualMovement - Math.abs(movement.salesMovement);
    });
    
    setProductMovements(Array.from(productMap.values()));
  };
  
  // Filter products based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredMovements(productMovements);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = productMovements.filter(item => 
      item.productName.toLowerCase().includes(query) || 
      item.productSku.toLowerCase().includes(query)
    );
    
    setFilteredMovements(filtered);
  }, [searchQuery, productMovements]);
  
  // Calculate totals based on filtered movements
  const totals = {
    beginningStock: filteredMovements.reduce((sum, item) => sum + item.beginningStock, 0),
    salesMovement: filteredMovements.reduce((sum, item) => sum + item.salesMovement, 0),
    purchaseMovement: filteredMovements.reduce((sum, item) => sum + item.purchaseMovement, 0),
    manualMovement: filteredMovements.reduce((sum, item) => sum + item.manualMovement, 0),
    endingStock: filteredMovements.reduce((sum, item) => sum + item.endingStock, 0),
    beginningValue: filteredMovements.reduce((sum, item) => sum + item.beginningValue, 0),
    endingValue: filteredMovements.reduce((sum, item) => sum + item.endingValue, 0)
  };
  
  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };
  
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Monthly Inventory Summary - {format(new Date(year, month, 1), 'MMMM yyyy')}
        </Typography>
        
        <TextField
          placeholder="Search by product name or SKU"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: '300px' }}
        />
      </Box>
      
      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Beginning Stock
            </Typography>
            <Typography variant="h5" component="div">
              {totals.beginningStock.toFixed(0)} units
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Purchases
            </Typography>
            <Typography variant="h5" component="div" color="success.main">
              +{totals.purchaseMovement.toFixed(0)} units
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Sales & Adjustments
            </Typography>
            <Typography variant="h5" component="div" color="error.main">
              {(totals.salesMovement + totals.manualMovement).toFixed(0)} units
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Ending Stock
            </Typography>
            <Typography variant="h5" component="div">
              {totals.endingStock.toFixed(0)} units
            </Typography>
          </CardContent>
        </Card>
      </Box>
      
      {/* Product Movement Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell align="right">Beginning Stock</TableCell>
              <TableCell align="right">Sales</TableCell>
              <TableCell align="right">Purchases</TableCell>
              <TableCell align="right">Manual Adj.</TableCell>
              <TableCell align="right">Ending Stock</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredMovements.length > 0 ? (
              filteredMovements.map((item) => (
                <TableRow key={item.productId}>
                  <TableCell>{item.productName}</TableCell>
                  <TableCell>{item.productSku}</TableCell>
                  <TableCell align="right">{item.beginningStock.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>
                    {item.salesMovement !== 0 ? item.salesMovement.toFixed(2) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>
                    {item.purchaseMovement !== 0 ? `+${item.purchaseMovement.toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ 
                    color: item.manualMovement >= 0 ? 'success.main' : 'error.main' 
                  }}>
                    {item.manualMovement !== 0 ? 
                      (item.manualMovement > 0 ? `+${item.manualMovement.toFixed(2)}` : item.manualMovement.toFixed(2)) 
                      : '-'}
                  </TableCell>
                  <TableCell align="right">{item.endingStock.toFixed(2)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {searchQuery ? "No products match your search" : "No inventory movements found for this month"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MonthlyInventorySummary;
